import { getSupabaseClient } from '../../data/supabase-client';
import { selectLocalNotifications } from '../../platform/local-notifications';
import { reportError } from '../../lib/monitoring';
import { diffMyDayNotifications, planMyDayNotifications } from './notification-plan';
import type { LocalNotificationsPort } from '../../platform/local-notifications';
import type { MyDayListRow } from '../../data/database.types';

/**
 * MY DAY NOTIFICATION RECONCILIATION — WD-3. The ONE path that makes the
 * device's pending notifications converge on server truth.
 *
 * **ONE PATH, MANY CALLERS, NO DRIFT.** App startup, foreground resume, a
 * successful My Day read, a manual mutation's reload and a Sol Confirm all
 * end up HERE — never at scattered scheduling calls that could disagree.
 * Every run is: authoritative rows → desired plan → diff against pending →
 * cancel obsolete → schedule missing. It is safe to run repeatedly: the diff
 * is idempotent, and single-flight coalescing below means overlapping calls
 * cannot interleave their cancels and schedules.
 *
 * **THE DATABASE STAYS THE AUTHORITY.** Rows come from `list_my_day_items`
 * under the caller's own RLS. Nothing here writes product state, and device
 * notification identifiers never travel anywhere but the OS.
 *
 * **PERMISSION IS CONTEXTUAL, AND NEVER NAGS** (this authorization):
 *
 *   * `mayPrompt: true` is passed ONLY from the moments a person just made a
 *     timed follow-up matter — creating one, or rescheduling onto a time —
 *     and prompts ONLY while the OS still says `prompt`.
 *   * Lifecycle runs pass `mayPrompt: false`: already-granted permission
 *     schedules normally; anything else degrades silently.
 *   * A DENIED state never re-prompts from code. If he later enables
 *     notifications in system settings, the next foreground reconciliation
 *     finds `granted` and schedules eligible items — nothing else needed.
 *   * Permission is NEVER a prerequisite for the follow-up itself: the rows
 *     are already saved server-side before this runs at all.
 *
 * **SESSION OWNERSHIP (review M2).** A local notification lives outside RLS
 * once it is on the device, so the session boundary is enforced HERE, from
 * the SAME session truth the whole app uses (the Supabase client's own
 * session — no second authentication system):
 *
 *   * every alert is scheduled FOR the signed-in user, carried as
 *     non-visible metadata;
 *   * reconciliation runs only WITH an authenticated user, considers only
 *     that user's rows, and cancels any Crewlink alert owned by a different
 *     user — or a legacy alert with no owner at all;
 *   * signing out cancels the session's Crewlink alerts IMMEDIATELY (the
 *     auth-state transition itself triggers it — no waiting for a foreground
 *     or a read), so User A's follow-up titles can never surface on User B's
 *     lock screen, restart or not;
 *   * a signed-out reconcile schedules nothing, ever;
 *   * and ONCE SIGN-OUT HAS BEEN OBSERVED, a reconciliation that began
 *     under the departed user can never re-arm that user's alerts (review
 *     R2): a session epoch advances synchronously at the sign-out
 *     observation, and every run re-checks it immediately before the native
 *     schedule step — an in-flight run whose epoch moved cancels safely but
 *     arms NOTHING.
 *
 * The owner id is matching metadata only: it is never visible content, never
 * database or RLS authority, and never a tap-mutation lever.
 *
 * **PARTIAL NATIVE FAILURE (review N1, accepted posture):** cancel and
 * schedule are two native calls, not a transaction. If the cancel lands and
 * the replacement schedule then fails, the device is temporarily missing an
 * alert — fail-closed, honestly incomplete — and the next reconciliation
 * run repairs it from server truth. Eventual repair, not atomicity, is the
 * contract; nothing here pretends otherwise.
 */

/**
 * **SESSION TRUTH IS THREE-VALUED (Slice B).**
 *
 * The accepted code answered `string | null`, which collapsed two completely
 * different facts into one: "nobody is signed in" and "I could not find out".
 * Only the first is authority for anything. Treating the second as a
 * signed-out state is how a live user's reminders got wiped, and treating it
 * as authenticated would be worse. So uncertainty gets its own name and is
 * allowed to authorise NOTHING.
 */
export type MyDaySessionTruth =
  | { readonly kind: 'authenticated'; readonly userId: string }
  | { readonly kind: 'signed_out' }
  | { readonly kind: 'unknown' };

export type MyDayNotificationRunStatus =
  | 'reconciled'
  | 'nothing_eligible'
  | 'unsupported'
  | 'permission_denied'
  | 'permission_not_asked'
  | 'signed_out'
  /** The session could not be read. Nothing was scheduled and nothing wiped. */
  | 'session_unknown'
  | 'unavailable';

export interface MyDayNotificationService {
  /** Fetch authoritative rows and reconcile. */
  run(options?: { readonly mayPrompt?: boolean }): Promise<MyDayNotificationRunStatus>;
  /** Reconcile from rows the caller ALREADY holds (a fresh read's result). */
  runWith(
    rows: readonly MyDayListRow[],
    options?: { readonly mayPrompt?: boolean },
  ): Promise<MyDayNotificationRunStatus>;
  /** Whether the honest "notifications are off" state applies right now. */
  isDenied(): Promise<boolean>;
  /**
   * The sign-out cleanup (M2): cancel every SCHEDULED Crewlink My Day
   * notification on this device, whoever owned it — they are all Crewlink's
   * — leaving anything Crewlink did not schedule untouched. Called from the
   * auth-state transition itself, never awaited behind a foreground.
   */
  clearAllOwned(): Promise<void>;
  /**
   * Watch the app's OWN session truth for the SIGNED_OUT transition and run
   * the cleanup immediately. Returns an unsubscribe. No-op off native.
   */
  watchSession(): () => void;
}

export interface MyDayNotificationServiceOptions {
  readonly port: LocalNotificationsPort;
  /** Injectable clock, so the lead boundary is provable. */
  readonly nowMs?: () => number;
  /** Injectable row loader; defaults to the authoritative reader. */
  readonly loadRows?: () => Promise<readonly MyDayListRow[] | null>;
  /**
   * The CURRENT session, from the app's own auth truth — three-valued, so an
   * unreadable session can never be mistaken for a signed-out one.
   */
  readonly loadSession?: () => Promise<MyDaySessionTruth>;
  /**
   * Subscribe to auth-state changes; the handler receives the event name.
   * Defaults to the Supabase client's own onAuthStateChange.
   */
  readonly onAuthChange?: (handler: (event: string) => void) => () => void;
  /**
   * Where passive diagnostic breadcrumbs go (Slice A). Defaults to the
   * device console, which Capacitor bridges to logcat. A sink that throws
   * cannot affect behaviour.
   */
  readonly trace?: MyDayNotificationTraceSink;
}

async function defaultLoadSession(): Promise<MyDaySessionTruth> {
  const client = getSupabaseClient();
  // No client at all is not proof that nobody is signed in.
  if (!client) return { kind: 'unknown' };
  try {
    const { data, error } = await client.auth.getSession();
    // An error is an ANSWERLESS read, not an answer of "signed out".
    if (error) return { kind: 'unknown' };
    const userId = data.session?.user.id;
    return userId === undefined ? { kind: 'signed_out' } : { kind: 'authenticated', userId };
  } catch {
    return { kind: 'unknown' };
  }
}

function defaultOnAuthChange(handler: (event: string) => void): () => void {
  const client = getSupabaseClient();
  if (!client) return () => {};
  const { data } = client.auth.onAuthStateChange((event) => handler(event));
  return () => data.subscription.unsubscribe();
}

async function defaultLoadRows(): Promise<readonly MyDayListRow[] | null> {
  const client = getSupabaseClient();
  if (!client) return null;
  const { data, error } = await client.rpc('list_my_day_items');
  if (error) {
    // **A FAILED READ RECONCILES NOTHING.** Cancelling the device schedule
    // because the network blinked would throw away real reminders; stale is
    // the honest offline posture, and the next successful run converges.
    reportError(error, 'myDay.notifications.read');
    return null;
  }
  return data ?? [];
}

/* ==========================================================================
 * SLICE A — PASSIVE DIAGNOSTIC BREADCRUMBS
 *
 * These exist to make the ACCEPTED-HEAD reconciliation decision spine
 * observable on a real phone, BEFORE the rebuild defect is touched. They
 * observe; they never steer.
 *
 * THE RULES THEY OBEY, AND WHY EACH ONE MATTERS:
 *   * SYNCHRONOUS AND NEVER AWAITED — a breadcrumb cannot introduce a yield
 *     point, so it cannot change an interleaving. Instrumentation that alters
 *     the race it is measuring is worse than none.
 *   * NEVER THROWS — the whole emit is wrapped. If the sink itself throws,
 *     production behaviour continues exactly as if the sink were absent.
 *   * NO EXTRA READS — no session read, no row read, no native read. Every
 *     value logged is one the code already had in hand.
 *   * NOTHING IS SCHEDULED, CANCELLED, RETRIED, TIMED OR QUEUED.
 *   * NO CONTROL FLOW AND NO RETURN VALUE CHANGES.
 *   * DEVICE-LOCAL ONLY — `console.info`, which Capacitor bridges to logcat.
 *     Never Sentry, never `reportError`, never any remote sink.
 *
 * WHAT IS NEVER LOGGED: user ids, item ids, titles, bodies, follow-up
 * content, due instants — anything private. Only counts, booleans, our own
 * synthetic run ids, epoch counters and a closed vocabulary of reasons.
 * ========================================================================== */

/** Bumped if the envelope shape changes, so old logcat captures stay readable. */
const TRACE_SCHEMA_VERSION = 1;

/** The logcat-greppable prefix: `adb logcat | grep CLNOTIF`. */
const TRACE_TAG = 'CLNOTIF';

export type MyDayNotificationTraceEvent =
  | 'auth.event'
  | 'reconcile.request'
  | 'session.read'
  | 'reconcile.decision'
  | 'native.operation'
  | 'epoch.fence'
  | 'reconcile.finish'
  | 'cleanup.finish';

export interface MyDayNotificationTraceRecord {
  readonly event: MyDayNotificationTraceEvent;
  /** Our own synthetic id for one run or cleanup — never a database id. */
  readonly id?: string;
  /** What asked for this: 'run' | 'runWith' | 'signed_out' | 'watch'. */
  readonly source?: string;
  /** Closed vocabulary; see the terminal reasons used below. */
  readonly reason?: string;
  /** True when this request joined a run already in flight instead of starting. */
  readonly joined?: boolean;
  readonly capturedEpoch?: number;
  readonly currentEpoch?: number;
  /** Counts only — never the things counted. */
  readonly eligible?: number;
  readonly onDevice?: number;
  readonly toCancel?: number;
  readonly toSchedule?: number;
  readonly ok?: boolean;
  /** Native op name for `native.operation`. */
  readonly op?: string;
  readonly phase?: 'start' | 'ok' | 'fail';
}

export type MyDayNotificationTraceSink = (line: string) => void;

const defaultTraceSink: MyDayNotificationTraceSink = (line) => {
  // Capacitor bridges the WebView console to logcat, which is the only sink
  // this slice is allowed to use. Deliberately NOT `reportError`: that is
  // remote monitoring, and a breadcrumb is device-local by contract.
  console.info(line);
};

export function createMyDayNotificationService(
  options: MyDayNotificationServiceOptions,
): MyDayNotificationService {
  const { port } = options;
  const now = options.nowMs ?? (() => Date.now());
  const loadRows = options.loadRows ?? defaultLoadRows;
  const loadSession = options.loadSession ?? defaultLoadSession;
  const onAuthChange = options.onAuthChange ?? defaultOnAuthChange;

  /**
   * SINGLE FLIGHT, PROMPT-AWARE. Reconciliation is cheap but not atomic — a
   * resume event and a mutation reload can land in the same breath, and two
   * interleaved cancel/schedule passes could duplicate. Overlapping calls
   * share one run — EXCEPT that a call carrying `mayPrompt` must not have its
   * prompt swallowed by joining a non-prompting run already in the air: it
   * chains a fresh run after the current one instead.
   */
  let inFlight: Promise<MyDayNotificationRunStatus> | null = null;
  let inFlightPrompts = false;

  /**
   * SESSION EPOCH (review R2). Sign-out cleanup and an in-flight
   * reconciliation are both async; without a boundary, a run that loaded
   * User A's rows before sign-out could reach its schedule step AFTER
   * `clearAllOwned()` and re-arm A's alerts. The epoch is that boundary:
   * it advances SYNCHRONOUSLY the moment sign-out is observed (before the
   * cleanup's first await), every run captures it before reading the
   * session, and the schedule step refuses to arm anything if it moved.
   * Cancelling stays allowed — cancels are always safe.
   */
  /**
   * **THE SESSION GENERATION (Slice B).** Advanced synchronously at every
   * observed session boundary — sign-out AND sign-in. It answers one
   * question: "was this work computed under the session that is current
   * now?" A run whose generation is stale may not touch the device, and a
   * request arriving after a boundary may not be served by work that began
   * before it.
   */
  let sessionGeneration = 0;

  /**
   * **ONE SERIALIZED NATIVE CRITICAL SECTION.** Every read/diff/cancel/
   * schedule — reconciliation and sign-out cleanup alike — takes a numbered
   * turn and runs alone. Cleanup running beside a rebuild is how a rebuild
   * came to observe an alarm that was already being cancelled, conclude the
   * device agreed, and arm nothing.
   *
   * Deliberately NO timeout: a native call that cannot actually be recalled
   * must not have its ordering authority released while it can still land.
   * A stalled native layer stalls the stream, and that is the honest
   * trade — the alternative silently permits out-of-order alarm mutation.
   */
  let queueTail: Promise<void> = Promise.resolve();
  let issuedTurn = 0;
  let executingTurn = 0;

  function enqueueDeviceWork<T>(work: (turn: number) => Promise<T>): Promise<T> {
    const turn = (issuedTurn += 1);
    const result = queueTail.then(() => {
      executingTurn = turn;
      return work(turn);
    });
    queueTail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  // ---- diagnostics (Slice A). Passive. See the contract above. -----------
  const trace = options.trace ?? defaultTraceSink;
  let traceSequence = 0;
  let traceIds = 0;
  const nextTraceId = (prefix: string): string => `${prefix}${(traceIds += 1)}`;

  /**
   * Emit one breadcrumb. Synchronous, never awaited, and wrapped so that a
   * throwing sink is indistinguishable from no sink at all.
   */
  function emit(record: MyDayNotificationTraceRecord): void {
    try {
      traceSequence += 1;
      trace(
        `[${TRACE_TAG}] ${JSON.stringify({
          v: TRACE_SCHEMA_VERSION,
          seq: traceSequence,
          ...record,
        })}`,
      );
    } catch {
      // A diagnostic may never change behaviour — including by failing.
    }
  }

  function emitSession(id: string, session: MyDaySessionTruth, generation: number): void {
    emit({
      event: 'session.read',
      id,
      reason: session.kind,
      capturedEpoch: generation,
      currentEpoch: sessionGeneration,
    });
  }

  /**
   * **THE AUTHORITY FENCE.** All three must hold, and the two SYNCHRONOUS
   * facts are read LAST so nothing can move between the final check and the
   * native call it guards.
   *
   *   * the session still resolves to the SAME authenticated user this work
   *     was computed for — `unknown` and `signed_out` both fail closed;
   *   * the generation has not advanced (a boundary was crossed);
   *   * this turn still owns the device.
   */
  async function stillAuthoritative(
    userId: string,
    generation: number,
    turn: number,
  ): Promise<boolean> {
    const session = await loadSession();
    if (session.kind !== 'authenticated' || session.userId !== userId) return false;
    return sessionGeneration === generation && executingTurn === turn;
  }

  /**
   * The whole device-touching critical section, as ONE ordered turn:
   * authority → current time → eligibility → read → diff → cancel → fence →
   * schedule.
   */
  async function reconcileOnDevice(
    rows: readonly MyDayListRow[],
    userId: string,
    generation: number,
    id: string,
  ): Promise<MyDayNotificationRunStatus> {
    return enqueueDeviceWork(async (turn) => {
      // Work that has lost authority mutates NOTHING — not even a cancel.
      // Ids are deterministic, so a stale run's cancel list names the very
      // id a fresh run just armed.
      if (!(await stillAuthoritative(userId, generation, turn))) {
        emit({ event: 'reconcile.finish', id, reason: 'authority_lost' });
        return 'signed_out';
      }

      // **ELIGIBILITY IS DECIDED WHEN THE TURN RUNS, NOT WHEN IT WAS QUEUED.**
      // A turn can wait; `createIfAbsent` must not be a frozen opinion from
      // before that wait, or a lead moment that expired while queued would be
      // armed anyway — the exact late catch-up B1 forbids.
      const desired = planMyDayNotifications(rows, now());
      emit({ event: 'reconcile.decision', id, reason: 'planned', eligible: desired.length });

      let scheduled;
      emit({ event: 'native.operation', id, op: 'getScheduled', phase: 'start' });
      try {
        scheduled = await port.getScheduled();
        emit({
          event: 'native.operation',
          id,
          op: 'getScheduled',
          phase: 'ok',
          onDevice: scheduled.length,
        });
      } catch (caught) {
        emit({ event: 'native.operation', id, op: 'getScheduled', phase: 'fail' });
        emit({ event: 'reconcile.finish', id, reason: 'scheduled_read_failed' });
        reportError(caught, 'myDay.notifications.scheduled');
        return 'unavailable';
      }

      // Authority can lapse while the read is open.
      if (!(await stillAuthoritative(userId, generation, turn))) {
        emit({ event: 'reconcile.finish', id, reason: 'authority_lost' });
        return 'signed_out';
      }

      // **REVALIDATE AT THE LATEST PRACTICAL MOMENT.** The plan above was
      // derived when the turn began; the device read that just finished can
      // itself take time, and a lead moment can expire inside it. Re-deriving
      // here — from the SAME rows but a fresh clock — is what guarantees that
      // an alert whose moment passed while we were reading is never armed.
      // Without this, `createIfAbsent` is a frozen opinion and B1's
      // no-late-catch-up rule holds only when the device happens to be quick.
      const revalidated = planMyDayNotifications(rows, now());
      if (revalidated.length !== desired.length) {
        emit({
          event: 'reconcile.decision',
          id,
          reason: 'revalidated',
          eligible: revalidated.length,
        });
      }
      const diff = diffMyDayNotifications(revalidated, scheduled, userId);
      emit({
        event: 'reconcile.decision',
        id,
        reason:
          diff.cancel.length === 0 && diff.schedule.length === 0 ? 'already_satisfied' : 'diffed',
        toCancel: diff.cancel.length,
        toSchedule: diff.schedule.length,
      });

      try {
        emit({
          event: 'native.operation',
          id,
          op: 'cancel',
          phase: 'start',
          toCancel: diff.cancel.length,
        });
        await port.cancel(diff.cancel);
        emit({ event: 'native.operation', id, op: 'cancel', phase: 'ok' });
      } catch (caught) {
        emit({ event: 'native.operation', id, op: 'cancel', phase: 'fail' });
        emit({ event: 'reconcile.finish', id, reason: 'cancel_failed' });
        reportError(caught, 'myDay.notifications.apply');
        return 'unavailable';
      }

      // **THE FINAL FENCE, IMMEDIATELY BEFORE ARMING.**
      if (!(await stillAuthoritative(userId, generation, turn))) {
        emit({
          event: 'epoch.fence',
          id,
          reason: 'authority_lost',
          capturedEpoch: generation,
          currentEpoch: sessionGeneration,
        });
        emit({ event: 'reconcile.finish', id, reason: 'authority_lost' });
        return 'signed_out';
      }

      try {
        emit({
          event: 'native.operation',
          id,
          op: 'schedule',
          phase: 'start',
          toSchedule: diff.schedule.length,
        });
        await port.schedule(diff.schedule);
        emit({ event: 'native.operation', id, op: 'schedule', phase: 'ok' });
      } catch (caught) {
        emit({ event: 'native.operation', id, op: 'schedule', phase: 'fail' });
        emit({ event: 'reconcile.finish', id, reason: 'schedule_failed' });
        reportError(caught, 'myDay.notifications.apply');
        return 'unavailable';
      }

      const reason = revalidated.length === 0 ? 'nothing_eligible' : 'schedule_applied';
      emit({ event: 'reconcile.finish', id, reason, toSchedule: diff.schedule.length });
      return revalidated.length === 0 ? 'nothing_eligible' : 'reconciled';
    });
  }

  async function reconcile(
    rows: readonly MyDayListRow[],
    mayPrompt: boolean,
    userId: string,
    generation: number,
    id: string,
  ): Promise<MyDayNotificationRunStatus> {
    if (!port.isSupported()) {
      emit({ event: 'reconcile.finish', id, reason: 'unsupported' });
      return 'unsupported';
    }

    // ---- PERMISSION: contextual, never nagging (unchanged). ---------------
    let permission = await port.checkPermission();
    if (permission === 'prompt' && mayPrompt) {
      permission = await port.requestPermission();
    }
    if (permission === 'denied') {
      emit({ event: 'reconcile.finish', id, reason: 'permission_denied' });
      return 'permission_denied';
    }
    if (permission !== 'granted') {
      const reason = permission === 'prompt' ? 'permission_not_asked' : 'unsupported';
      emit({ event: 'reconcile.finish', id, reason });
      return permission === 'prompt' ? 'permission_not_asked' : 'unsupported';
    }

    return reconcileOnDevice(rows, userId, generation, id);
  }

  /** Cancel every SCHEDULED Crewlink alert — as an ordered turn, like everything else. */
  function enqueueCleanup(id: string): Promise<void> {
    return enqueueDeviceWork(async () => {
      if (!port.isSupported()) {
        emit({ event: 'cleanup.finish', id, reason: 'unsupported' });
        return;
      }
      try {
        emit({ event: 'native.operation', id, op: 'getScheduled', phase: 'start' });
        const scheduled = await port.getScheduled();
        emit({
          event: 'native.operation',
          id,
          op: 'getScheduled',
          phase: 'ok',
          onDevice: scheduled.length,
        });
        const ours = scheduled.filter((entry) => entry.itemId !== null).map((entry) => entry.id);
        emit({
          event: 'native.operation',
          id,
          op: 'cancel',
          phase: 'start',
          toCancel: ours.length,
        });
        await port.cancel(ours);
        emit({ event: 'native.operation', id, op: 'cancel', phase: 'ok' });
        emit({ event: 'cleanup.finish', id, reason: 'cleaned', toCancel: ours.length });
      } catch (caught) {
        emit({ event: 'native.operation', id, op: 'cleanup', phase: 'fail' });
        emit({ event: 'cleanup.finish', id, reason: 'cleanup_failed' });
        reportError(caught, 'myDay.notifications.signout');
      }
    });
  }

  /**
   * **CROSSING A SESSION BOUNDARY.** Synchronous, so a boundary can never
   * lose a race to work already in the air. It does two things and no more:
   * invalidates every in-flight run, and detaches the coalescing slot so a
   * request arriving AFTER the boundary starts its own run instead of being
   * served by pre-boundary work. Cross-session coalescing is an optimisation;
   * correctness outranks it.
   */
  function crossSessionBoundary(): void {
    sessionGeneration += 1;
    inFlight = null;
    inFlightPrompts = false;
  }

  /** The public cleanup primitive: cross the boundary, then clean in order. */
  async function clearAllOwned(): Promise<void> {
    const id = nextTraceId('cleanup');
    crossSessionBoundary();
    emit({ event: 'reconcile.request', id, source: 'cleanup', currentEpoch: sessionGeneration });
    await enqueueCleanup(id);
  }

  /**
   * A SIGNED_OUT event, weighed against authoritative session truth.
   *
   * The event is the authority for cleaning; the re-read exists only to
   * VETO it. And only one answer can veto: a live authenticated session,
   * which proves the event is stale (the user is already back). `unknown`
   * proves nothing, so it cannot overturn a sign-out that really happened —
   * while on its own, with no SIGNED_OUT event, uncertainty never triggers
   * cleanup at all. Privacy is preserved without letting doubt destroy.
   */
  async function handleSignedOut(): Promise<void> {
    const id = nextTraceId('cleanup');
    emit({ event: 'reconcile.request', id, source: 'signed_out', currentEpoch: sessionGeneration });
    const session = await loadSession();
    emitSession(id, session, sessionGeneration);
    if (session.kind === 'authenticated') {
      emit({ event: 'cleanup.finish', id, reason: 'stale_event_ignored' });
      // The boundary may have invalidated a legitimate run; replace it.
      void runReconciliation(false, 'stale_signout_repair');
      return;
    }
    await enqueueCleanup(id);
  }

  function coalesce(
    work: () => Promise<MyDayNotificationRunStatus>,
    wantsPrompt: boolean,
  ): Promise<MyDayNotificationRunStatus> {
    if (inFlight !== null) {
      if (!wantsPrompt || inFlightPrompts) return inFlight;
      // Each settle clears ONLY its own run: detached or superseded work must
      // never null out the newer slot that replaced it.
      const chained: Promise<MyDayNotificationRunStatus> = inFlight
        .then(() => work())
        .finally(() => {
          if (inFlight === chained) {
            inFlight = null;
            inFlightPrompts = false;
          }
        });
      inFlight = chained;
      inFlightPrompts = true;
      return chained;
    }
    inFlightPrompts = wantsPrompt;
    const started: Promise<MyDayNotificationRunStatus> = work().finally(() => {
      if (inFlight === started) {
        inFlight = null;
        inFlightPrompts = false;
      }
    });
    inFlight = started;
    return started;
  }

  /** One full run: session truth → authoritative rows → ordered device turn. */
  function runReconciliation(
    mayPrompt: boolean,
    source: string,
  ): Promise<MyDayNotificationRunStatus> {
    const id = nextTraceId('run');
    emit({ event: 'reconcile.request', id, source, joined: inFlight !== null });
    return coalesce(async () => {
      if (!port.isSupported()) {
        emit({ event: 'reconcile.finish', id, reason: 'unsupported' });
        return 'unsupported';
      }
      const generation = sessionGeneration;
      const session = await loadSession();
      emitSession(id, session, generation);
      if (session.kind === 'unknown') {
        // Uncertainty authorises nothing: no scheduling, no cancelling.
        emit({ event: 'reconcile.finish', id, reason: 'session_unknown' });
        return 'session_unknown';
      }
      if (session.kind === 'signed_out') {
        emit({ event: 'reconcile.finish', id, reason: 'no_session' });
        return 'signed_out';
      }
      const rows = await loadRows();
      if (rows === null) {
        emit({ event: 'reconcile.finish', id, reason: 'rows_unavailable' });
        return 'unavailable';
      }
      return reconcile(rows, mayPrompt, session.userId, generation, id);
    }, mayPrompt);
  }

  function runReconciliationWith(
    rows: readonly MyDayListRow[],
    mayPrompt: boolean,
    source: string,
  ): Promise<MyDayNotificationRunStatus> {
    const id = nextTraceId('runWith');
    emit({ event: 'reconcile.request', id, source, joined: inFlight !== null });
    return coalesce(async () => {
      if (!port.isSupported()) {
        emit({ event: 'reconcile.finish', id, reason: 'unsupported' });
        return 'unsupported';
      }
      const generation = sessionGeneration;
      const session = await loadSession();
      emitSession(id, session, generation);
      if (session.kind === 'unknown') {
        emit({ event: 'reconcile.finish', id, reason: 'session_unknown' });
        return 'session_unknown';
      }
      if (session.kind === 'signed_out') {
        emit({ event: 'reconcile.finish', id, reason: 'no_session' });
        return 'signed_out';
      }
      return reconcile(rows, mayPrompt, session.userId, generation, id);
    }, mayPrompt);
  }

  return {
    run(runOptions) {
      return runReconciliation(runOptions?.mayPrompt === true, 'run');
    },

    runWith(rows, runOptions) {
      return runReconciliationWith(rows, runOptions?.mayPrompt === true, 'runWith');
    },

    async isDenied() {
      if (!port.isSupported()) return false;
      return (await port.checkPermission()) === 'denied';
    },

    clearAllOwned,

    watchSession() {
      if (!port.isSupported()) return () => {};
      return onAuthChange((event) => {
        emit({ event: 'auth.event', source: event, currentEpoch: sessionGeneration });

        if (event === 'SIGNED_OUT') {
          // Synchronous first: a real sign-out cannot lose the race.
          crossSessionBoundary();
          void handleSignedOut();
          return;
        }

        // **EVERY AUTHENTICATED TRANSITION RECONCILES (Slice B).** Not only a
        // CHANGE of identity: the defect Jerry reproduced was a SAME-user
        // sign-in, where an identity gate is exactly the thing that decides
        // "nothing changed" and rebuilds nothing. Reconciliation is
        // idempotent, so the cost of an extra run is a no-op diff, while the
        // cost of a missed one is a reminder that never comes back.
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
          crossSessionBoundary();
          void runReconciliation(false, event);
        }
      });
    },
  };
}

/* ==========================================================================
 * The app-wide instance — one service, one port, chosen once
 * ========================================================================== */

let shared: MyDayNotificationService | null = null;
let sharedFactory: (() => MyDayNotificationService) | null = null;

/** Tests replace the shared instance; production uses the real port. */
export function setMyDayNotificationServiceFactory(
  factory: (() => MyDayNotificationService) | null,
): void {
  sharedFactory = factory;
  shared = null;
}

export function myDayNotificationService(): MyDayNotificationService {
  if (shared === null) {
    if (sharedFactory !== null) {
      shared = sharedFactory();
    } else {
      // Imported lazily-by-module: `src/platform` is the one place the
      // Capacitor import lives; this module only ever sees the port.
      shared = createMyDayNotificationService({ port: defaultPort() });
    }
  }
  return shared;
}

let port: LocalNotificationsPort | null = null;
function defaultPort(): LocalNotificationsPort {
  if (port === null) port = selectLocalNotifications();
  return port;
}

/** The selected port, for the lifecycle bridge (tap + foreground wiring). */
export function myDayNotificationPort(): LocalNotificationsPort {
  return defaultPort();
}

/** Tests replace the port the bridge and default service see. */
export function setMyDayNotificationPort(override: LocalNotificationsPort | null): void {
  port = override;
  shared = null;
}
